import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversationsApi, Conversation } from '../services/conversationsApi';
import { fmtLongDay, fmtTime, istToday, parseChatTime, shiftDay } from '../lib';
import { Empty, Failed, Loading } from '../components/WorkBits';
import WhatsAppChat from '../components/WhatsAppChat';
import LeadGlance from '../components/lead/LeadGlance';

/** The operator's WhatsApp conversations on the Bhuvana line, one IST day at
 *  a time — the Nurture app's Messages tab, with the chat and the lead beside
 *  the list instead of a screen push. The chat server scopes the list to the
 *  signed-in operator. Updates on Refresh; there is no live push. */

const name = (c: Conversation) => c.recipient_name || c.title || `Chat ${c.id.slice(0, 6)}`;

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [day, setDay] = useState(istToday());
  const [list, setList] = useState<Conversation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Conversation | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const from = new Date(`${day}T00:00:00+05:30`);
      const to = new Date(`${shiftDay(day, 1)}T00:00:00+05:30`);
      const { conversations } = await conversationsApi.getConversations({ from, to }, 200, 0);
      setList(conversations);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [day]);

  useEffect(() => { setList(null); setSelected(null); load(); }, [load]);

  const isToday = day === istToday();
  const q = search.trim().toLowerCase();
  const shown = (list || []).filter((c) => !q || name(c).toLowerCase().includes(q) || (c.recipient_id || '').includes(q));

  return (
    <div className="w-full px-7 pt-6 pb-6 h-full flex flex-col">
      <div className="page-head">
        <div>
          <h1 className="page-title">Messages</h1>
          <p className="page-sub">Your WhatsApp conversations on the Bhuvana line · times IST</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="icb" aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))}>‹</button>
          <b className="font-display text-[14px] min-w-[190px] text-center">
            {isToday ? 'Today · ' : ''}{fmtLongDay(`${day}T12:00:00+05:30`)}
          </b>
          <button className="icb" aria-label="Next day" disabled={isToday} onClick={() => setDay(shiftDay(day, 1))}>›</button>
          <button className="btn ml-2" onClick={load}>Refresh</button>
        </div>
      </div>

      {error && <Failed what="messages" error={error} />}

      <div className="flex-1 min-h-[520px] grid grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_280px]
                      border-[1.5px] border-sketch rounded-lg bg-warm-white shadow-sketch overflow-hidden">
        <div className="flex flex-col min-h-0 border-r-[1.5px] border-sketch-soft">
          <div className="p-3 border-b-[1.5px] border-sketch-soft">
            <input className="inp w-full" placeholder="Search name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto">
            {!list && !error && <Loading what="conversations" />}
            {list && shown.length === 0 && <Empty>{list.length ? 'No match.' : 'No conversations on this day.'}</Empty>}
            {shown.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 border-b border-dotted border-sketch-soft text-left
                  ${selected?.id === c.id ? 'bg-sage/20' : 'hover:bg-cream'}`}
              >
                <span className="avat w-9 h-9 text-[13px]">{name(c).charAt(0).toUpperCase()}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    <b className="text-[13.5px] truncate">{name(c)}</b>
                    {c.in_live_agent_mode && <span className="chip chip-gold !text-[10px] !px-1.5">Live agent</span>}
                  </span>
                  <span className="block text-[12px] text-graphite truncate">{c.last_message}</span>
                </span>
                <span className="flex flex-col items-end gap-1 flex-none">
                  {c.last_message_time && <span className="num text-[11px] text-graphite">{fmtTime(parseChatTime(c.last_message_time).toISOString())}</span>}
                  {c.unread_count > 0 && (
                    <span className="num text-[10.5px] font-bold bg-forest text-warm-white rounded-full px-1.5">
                      {c.unread_count > 99 ? '99+' : c.unread_count}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 min-w-0 border-r-[1.5px] border-sketch-soft flex flex-col">
          {selected?.lead_id && (
            <div className="xl:hidden px-3 py-1.5 border-b-[1.5px] border-sketch-soft text-right">
              <button className="text-forest text-[12.5px] font-bold" onClick={() => navigate(`/leads/${selected.lead_id}`)}>
                Lead page ›
              </button>
            </div>
          )}
          <div className="flex-1 min-h-0">
          {selected ? (
            <WhatsAppChat
              key={selected.id}
              inline
              conversationId={selected.id}
              title={name(selected)}
              subtitle={selected.recipient_id}
              onClose={() => setSelected(null)}
              onLiveAgentModeChanged={(id, live) =>
                setList((prev) => prev && prev.map((c) => (c.id === id ? { ...c, in_live_agent_mode: live } : c)))}
            />
          ) : (
            <div className="h-full grid place-items-center text-graphite text-[13px]">Pick a conversation.</div>
          )}
          </div>
        </div>

        <div className="hidden xl:block min-h-0 overflow-y-auto p-4">
          {selected?.lead_id ? <LeadGlance key={selected.lead_id} leadId={selected.lead_id} />
            : selected ? <p className="text-graphite text-[13px]">This conversation isn't linked to a lead.</p>
              : null}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
